import React, { useMemo, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Trans } from "react-i18next";
import { useDateFormatter } from "../../Components/FormatDateTime";
import { getBatchProgress, buildBatchMetaByBatchNo } from "./audienceBatchProgress";
import "../../Styles/pages/product.scss";

const ROWS_PER_BATCH = 20;

const AudienceCsvEntriesAccordion = ({
    entries = [],
    columns = [],
    loading = false,
    isMobileStack = false,
    renderMobileSummary,
    onProcessBatch = () => { },
    batches = [],
    batchesLoading = false,
    applyingBatchNos = [],
    t,
}) => {
    const [activeIndex, setActiveIndex] = useState([]);
    const [batchPagination, setBatchPagination] = useState({});
    const { formatDate } = useDateFormatter();
    const batchMetaByBatchNo = useMemo(() => buildBatchMetaByBatchNo(batches), [batches]);

    const entriesByBatch = useMemo(() => {
        const map = new Map();
        entries.forEach((entry) => {
            const key = entry.batchNo ?? "";
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key).push(entry);
        });
        return [...map.entries()].sort(([a], [b]) => String(a).localeCompare(String(b)));
    }, [entries]);

    const getBatchColumns = (batchColumns) => {
        if (!isMobileStack) {
            return batchColumns;
        }

        return [{
            field: "mobileSummary",
            body: (rowData) => renderMobileSummary(batchColumns, rowData),
            className: "audience-detail-mobile-summary-column",
        }];
    };

    const getBatchPageState = (batchKey) => (
        batchPagination[batchKey] || { first: 0, rows: ROWS_PER_BATCH }
    );

    const onBatchPageChange = (batchKey, e) => {
        setBatchPagination((prev) => ({
            ...prev,
            [batchKey]: { first: e.first, rows: e.rows },
        }));
    };

    if ((loading || batchesLoading) && !entries.length) {
        return (
            <div className="audience-csv-entries-accordion audience-csv-entries-loading flex justify-content-center p-4">
                <ProgressSpinner style={{ width: "40px", height: "40px" }} />
            </div>
        );
    }

    if (!entriesByBatch.length) {
        return (
            <div className="audience-csv-entries-accordion audience-csv-entries-empty p-3">
                <p className="mb-0">{t("business_customers.no_imported_entries")}</p>
            </div>
        );
    }

    return (
        <div className="audience-csv-entries-accordion">
            <Accordion
                activeIndex={activeIndex}
                multiple
                onTabChange={(e) => setActiveIndex(e.index)}
                className="audience-csv-accordion mb-3 mt-2"
            >
                {entriesByBatch.map(([batchNo, batchRows]) => {
                    const batchKey = batchNo || "unbatched";
                    const batchColumns = getBatchColumns(columns);
                    const { first, rows } = getBatchPageState(batchKey);
                    const batchMeta = batchMetaByBatchNo.get(String(batchNo ?? ""));
                    const recordCount = batchMeta?.entryCount ?? batchRows.length;
                    const completedOnFormatted = batchMeta?.completedOn
                        ? formatDate(batchMeta.completedOn)
                        : null;
                    const progress = getBatchProgress({
                        batchMeta,
                        batchRows,
                        batchNo,
                        applyingBatchNos,
                    });
                    const statusBadge = (
                        <span className={`planDetailCard-badge ${progress.className}`}>
                            {t(progress.labelKey).toUpperCase()}
                        </span>
                    );
                    const progressText = (
                        <p className="audience-csv-batch-progress-text mb-0">
                            {t("business_customers.batch_progress", {
                                imported: progress.importedCount,
                                total: progress.total,
                            })}
                        </p>
                    );

                    const isApplying = applyingBatchNos.some(
                        (no) => String(no) === String(batchNo ?? "")
                    );
                    const showProcessAction = progress.status !== "successful" && progress.status !== "in-progress";

                    return (
                        <AccordionTab
                            key={batchKey}
                            header={
                                <div className="audience-csv-batch-header subscription-body">
                                    <div className="audience-csv-batch-header-row flex justify-content-between align-items-start w-full">
                                        <div className="audience-csv-batch-header-info">
                                            <div className="audience-csv-batch-header-batch">
                                                <Trans
                                                    i18nKey="business_customers.accordion_batch_header"
                                                    values={{ batchNo: batchNo || "—" }}
                                                    components={{
                                                        1: <span className="name-body" />,
                                                    }}
                                                />
                                                <p className="plan-desc mb-0 audience-csv-batch-record-count">
                                                    <Trans
                                                        i18nKey="business_customers.accordion_batch_count"
                                                        values={{ count: recordCount }}
                                                        components={{ 1: <span /> }}
                                                    />
                                                </p>
                                            </div>
                                            <div className="audience-csv-batch-header-status-col">
                                                {completedOnFormatted ? (
                                                    <div className="createdOnBodyTemplate audience-csv-batch-completed-on mb-1">
                                                        <span className="product_date">{t("business_customers.completedOn")}{":"}</span>{" "}
                                                        <span className="productModifiedDate">{completedOnFormatted}</span>
                                                    </div>
                                                ) : null}
                                                {statusBadge}
                                                {progressText}
                                            </div>
                                        </div>
                                        {showProcessAction && (
                                            <div
                                                className="audience-csv-batch-process-actions car-buttons tier_action_button"
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <Button
                                                    type="button"
                                                    icon="pi pi-cog"
                                                    className="p-button active-action-button audience-csv-batch-process-button"
                                                    tooltip={t("business_customers.process_batch_tooltip")}
                                                    tooltipOptions={{
                                                        position: "bottom",
                                                        mouseTrack: true,
                                                        mouseTrackTop: 15,
                                                    }}
                                                    loading={isApplying}
                                                    disabled={isApplying}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onProcessBatch(batchNo);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                        >
                            <h6 className="group-name products-plans-heading audience-csv-batch-records-heading">
                                {t("business_customers.audience_csv_batch_records")}
                            </h6>
                            <DataTable
                                value={batchRows}
                                dataKey="id"
                                paginator={batchRows.length > ROWS_PER_BATCH}
                                first={first}
                                rows={rows}
                                totalRecords={batchRows.length}
                                onPage={(e) => onBatchPageChange(batchKey, e)}
                                loading={loading}
                                breakpoint="1050px"
                                responsiveLayout="stack"
                                scrollable={!isMobileStack}
                                scrollHeight={isMobileStack ? undefined : "flex"}
                                className="p-datatable-responsive-table expanded audience-csv-expanded-table"
                            >
                                {batchColumns.map((col, i) => (
                                    <Column
                                        key={col.field || i}
                                        field={col.field}
                                        header={col.header}
                                        body={col.body}
                                        alignHeader={col.alignHeader || "left"}
                                        style={col.style}
                                        bodyClassName={col.bodyClassName}
                                        className={`datatable-cell-default ${col.className || ""}`}
                                        headerClassName={col.headerClassName || "datatable-header-default header-style"}
                                    />
                                ))}
                            </DataTable>
                        </AccordionTab>
                    );
                })}
            </Accordion>
        </div>
    );
};

export default AudienceCsvEntriesAccordion;
